/**
 * Filter API client for managing hierarchical filters.
 *
 * Provides functions to interact with the filter configuration endpoints.
 */

import { apiGet, apiPost, apiPut, apiDelete } from './api'

// =============================================================================
// Types
// =============================================================================

/**
 * Global filter configuration.
 */
export interface GlobalFilterConfig {
  id: string
  filterConfigId: string
  filterKey: string
  label: string
  filterType: 'multi_select' | 'search' | 'date_range'
  sourceColumn: string
  sortOrder: number
  isEnabled: boolean
  cachedValues?: string[]
  lastCachedAt?: string
  createdAt: string
  updatedAt: string
}

/**
 * Dataset link configuration for global filter application.
 */
export interface DatasetLink {
  id: string
  filterConfigId: string
  datasetName: string
  subjectIdColumn: string
  createdAt: string
  updatedAt: string
}

/**
 * Study filter configuration.
 */
export interface StudyFilterConfig {
  id: string
  studyId: string
  subjectMasterDataset: string
  subjectIdColumn: string
  globalFilters: GlobalFilterConfig[]
  datasetLinks: DatasetLink[]
  createdAt: string
  updatedAt: string
}

/**
 * Page filter configuration.
 */
export interface PageFilterConfig {
  id: string
  pageId: number
  filterKey: string
  label: string
  filterType: 'multi_select' | 'search'
  sourceDataset: string
  sourceColumn: string
  sortOrder: number
  isEnabled: boolean
  defaultValues?: string[]
  createdAt: string
  updatedAt: string
}

/**
 * Filter definition for saved filters.
 */
export interface FilterDefinition {
  globalFilters: Record<string, string | string[] | { start?: string; end?: string }>
  localFilters: Record<string, Record<string, string | string[]>>
}

/**
 * Saved filter.
 */
export interface SavedFilter {
  id: string
  studyId: string
  name: string
  description?: string
  filterDefinition: FilterDefinition
  pageKey?: string
  createdBy: string
  isShared: boolean
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Brief saved filter for listing.
 */
export interface SavedFilterBrief {
  id: string
  name: string
  description?: string
  pageKey?: string
  isShared: boolean
  isDefault: boolean
  createdBy: string
  createdAt: string
}

/**
 * Filter values response for cascading filters.
 */
export interface FilterValuesResponse {
  values: Record<string, string[]>
}

// =============================================================================
// Request Types
// =============================================================================

export interface CreateStudyFilterConfigRequest {
  subject_master_dataset: string
  subject_id_column: string
  global_filters?: Array<{
    filter_key: string
    label: string
    filter_type: 'multi_select' | 'search' | 'date_range'
    source_column: string
    sort_order?: number
    is_enabled?: boolean
  }>
  dataset_links?: Array<{
    dataset_name: string
    subject_id_column: string
  }>
}

export interface UpdateStudyFilterConfigRequest {
  subject_master_dataset?: string
  subject_id_column?: string
}

export interface CreateGlobalFilterRequest {
  filter_key: string
  label: string
  filter_type: 'multi_select' | 'search' | 'date_range'
  source_column: string
  sort_order?: number
  is_enabled?: boolean
}

export interface UpdateGlobalFilterRequest {
  label?: string
  filter_type?: 'multi_select' | 'search' | 'date_range'
  source_column?: string
  sort_order?: number
  is_enabled?: boolean
}

export interface CreateDatasetLinkRequest {
  dataset_name: string
  subject_id_column: string
}

export interface CreatePageFilterRequest {
  page_id: number
  filter_key: string
  label: string
  filter_type: 'multi_select' | 'search'
  source_dataset: string
  source_column: string
  sort_order?: number
  is_enabled?: boolean
  default_values?: string[]
}

export interface UpdatePageFilterRequest {
  label?: string
  filter_type?: 'multi_select' | 'search'
  source_dataset?: string
  source_column?: string
  sort_order?: number
  is_enabled?: boolean
  default_values?: string[]
}

export interface CreateSavedFilterRequest {
  name: string
  description?: string
  filter_definition: FilterDefinition
  page_key?: string
  is_shared?: boolean
  is_default?: boolean
}

export interface UpdateSavedFilterRequest {
  name?: string
  description?: string
  filter_definition?: FilterDefinition
  page_key?: string
  is_shared?: boolean
  is_default?: boolean
}

export interface GetFilterValuesRequest {
  current_filters: Record<string, string | string[] | { start?: string; end?: string }>
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get study filter configuration.
 */
export async function getStudyFilterConfig(studyId: string): Promise<StudyFilterConfig | null> {
  const response = await apiGet<StudyFilterConfig | null>(`/studies/${studyId}/filter-config`)
  return response.data
}

/**
 * Create study filter configuration.
 */
export async function createStudyFilterConfig(
  studyId: string,
  data: CreateStudyFilterConfigRequest
): Promise<StudyFilterConfig> {
  const response = await apiPost<StudyFilterConfig>(`/studies/${studyId}/filter-config`, data)
  return response.data
}

/**
 * Update study filter configuration.
 */
export async function updateStudyFilterConfig(
  studyId: string,
  data: UpdateStudyFilterConfigRequest
): Promise<StudyFilterConfig> {
  const response = await apiPut<StudyFilterConfig>(`/studies/${studyId}/filter-config`, data)
  return response.data
}

/**
 * Delete study filter configuration.
 */
export async function deleteStudyFilterConfig(studyId: string): Promise<void> {
  await apiDelete(`/studies/${studyId}/filter-config`)
}

/**
 * Add a global filter to the study configuration.
 */
export async function addGlobalFilter(
  studyId: string,
  data: CreateGlobalFilterRequest
): Promise<GlobalFilterConfig> {
  const response = await apiPost<GlobalFilterConfig>(
    `/studies/${studyId}/filter-config/global-filters`,
    data
  )
  return response.data
}

/**
 * Update a global filter.
 */
export async function updateGlobalFilter(
  studyId: string,
  filterId: string,
  data: UpdateGlobalFilterRequest
): Promise<GlobalFilterConfig> {
  const response = await apiPut<GlobalFilterConfig>(
    `/studies/${studyId}/filter-config/global-filters/${filterId}`,
    data
  )
  return response.data
}

/**
 * Delete a global filter.
 */
export async function deleteGlobalFilter(studyId: string, filterId: string): Promise<void> {
  await apiDelete(`/studies/${studyId}/filter-config/global-filters/${filterId}`)
}

/**
 * Add a dataset link to the study configuration.
 */
export async function addDatasetLink(
  studyId: string,
  data: CreateDatasetLinkRequest
): Promise<DatasetLink> {
  const response = await apiPost<DatasetLink>(
    `/studies/${studyId}/filter-config/dataset-links`,
    data
  )
  return response.data
}

/**
 * Delete a dataset link.
 */
export async function deleteDatasetLink(studyId: string, linkId: string): Promise<void> {
  await apiDelete(`/studies/${studyId}/filter-config/dataset-links/${linkId}`)
}

/**
 * Get page filter configurations.
 */
export async function getPageFilters(studyId: string, pageId: number): Promise<PageFilterConfig[]> {
  const response = await apiGet<PageFilterConfig[]>(`/studies/${studyId}/pages/${pageId}/filters`)
  return response.data
}

/**
 * Create a page filter.
 */
export async function createPageFilter(
  studyId: string,
  pageId: number,
  data: CreatePageFilterRequest
): Promise<PageFilterConfig> {
  const response = await apiPost<PageFilterConfig>(
    `/studies/${studyId}/pages/${pageId}/filters`,
    data
  )
  return response.data
}

/**
 * Update a page filter.
 */
export async function updatePageFilter(
  studyId: string,
  pageId: number,
  filterId: string,
  data: UpdatePageFilterRequest
): Promise<PageFilterConfig> {
  const response = await apiPut<PageFilterConfig>(
    `/studies/${studyId}/pages/${pageId}/filters/${filterId}`,
    data
  )
  return response.data
}

/**
 * Delete a page filter.
 */
export async function deletePageFilter(
  studyId: string,
  pageId: number,
  filterId: string
): Promise<void> {
  await apiDelete(`/studies/${studyId}/pages/${pageId}/filters/${filterId}`)
}

/**
 * List saved filters for the current user.
 */
export async function listSavedFilters(
  studyId: string,
  pageKey?: string
): Promise<SavedFilterBrief[]> {
  const params = pageKey ? { pageKey } : undefined
  const response = await apiGet<SavedFilterBrief[]>(`/studies/${studyId}/saved-filters`, params)
  return response.data
}

/**
 * Get a specific saved filter.
 */
export async function getSavedFilter(studyId: string, filterId: string): Promise<SavedFilter> {
  const response = await apiGet<SavedFilter>(`/studies/${studyId}/saved-filters/${filterId}`)
  return response.data
}

/**
 * Create a saved filter.
 */
export async function createSavedFilter(
  studyId: string,
  data: CreateSavedFilterRequest
): Promise<SavedFilter> {
  const response = await apiPost<SavedFilter>(`/studies/${studyId}/saved-filters`, data)
  return response.data
}

/**
 * Update a saved filter.
 */
export async function updateSavedFilter(
  studyId: string,
  filterId: string,
  data: UpdateSavedFilterRequest
): Promise<SavedFilter> {
  const response = await apiPut<SavedFilter>(
    `/studies/${studyId}/saved-filters/${filterId}`,
    data
  )
  return response.data
}

/**
 * Delete a saved filter.
 */
export async function deleteSavedFilter(studyId: string, filterId: string): Promise<void> {
  await apiDelete(`/studies/${studyId}/saved-filters/${filterId}`)
}

/**
 * Get user's default saved filter for a study.
 * Returns null if no default filter is set.
 */
export async function getDefaultSavedFilter(
  studyId: string,
  pageKey?: string
): Promise<SavedFilter | null> {
  try {
    const params = pageKey ? { pageKey } : undefined
    const response = await apiGet<SavedFilter | null>(
      `/studies/${studyId}/saved-filters/default`,
      params
    )
    return response.data
  } catch {
    // No default filter set - return null
    return null
  }
}

/**
 * Get filter values with cascading support.
 */
export async function getFilterValues(
  studyId: string,
  currentFilters: Record<string, string | string[] | { start?: string; end?: string }> = {}
): Promise<FilterValuesResponse> {
  const response = await apiPost<FilterValuesResponse>(
    `/studies/${studyId}/filter-values`,
    { currentFilters }
  )
  return response.data
}

/**
 * Trigger filter value cache refresh (admin only).
 */
export async function refreshFilterValues(studyId: string): Promise<void> {
  await apiPost(`/studies/${studyId}/filter-values/refresh`)
}

/**
 * Get local (page-specific) filter values.
 */
export async function getLocalFilterValues(
  studyId: string,
  pageId: number
): Promise<FilterValuesResponse> {
  const response = await apiGet<FilterValuesResponse>(
    `/studies/${studyId}/pages/${pageId}/filter-values`
  )
  return response.data
}
